import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import api from "@/lib/api";
import { Instagram, Facebook, Share2, ArrowUpRight, Users, Gauge, Route } from "lucide-react";

const CATS = ["All", "Light Jet", "Midsize Jet", "Super Midsize", "Heavy Jet", "Ultra Long Range", "VIP Airliner"];

export default function CharterMarketplace() {
  const [fleet, setFleet] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/fleet").then(r => setFleet(r.data)).finally(() => setLoading(false));
  }, []);

  const list = filter === "All" ? fleet : fleet.filter(a => a.category === filter);

  const share = (aircraft, platform) => {
    const url = `${window.location.origin}/aircraft/${aircraft.id}`;
    const text = `Charter the ${aircraft.manufacturer} ${aircraft.model} with AeroSphere Global — ${aircraft.range_nm} nm range, ${aircraft.max_passengers} passengers.`;
    let target = "";
    if (platform === "instagram") {
      // Instagram doesn't support direct share URLs; copy to clipboard
      navigator.clipboard.writeText(`${text} ${url}`);
      alert("Copied to clipboard for Instagram sharing");
      return;
    }
    if (platform === "facebook") {
      target = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
    }
    if (platform === "share") {
      if (navigator.share) { navigator.share({ title: aircraft.model, text, url }); return; }
      navigator.clipboard.writeText(`${text} ${url}`);
      alert("Link copied");
      return;
    }
    window.open(target, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-[#FAF7F0] text-[#1A1D24]">
      <PublicNav />

      {/* HERO */}
      <section className="relative overflow-hidden as-grain border-b border-[#E5DFD2]">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/20562279/pexels-photo-20562279.jpeg?auto=compress&cs=tinysrgb&w=1800" alt="" className="w-full h-full object-cover"/>
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#1A1D24] via-[#1A1D24]/70 to-[#1A1D24]/20"/>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 grid grid-cols-1 md:grid-cols-12 gap-8 text-white">
          <div className="md:col-span-8 as-fade-up">
            <div className="as-overline mb-5 !text-white/60">Charter Marketplace · Live Fleet</div>
            <h1 className="as-serif text-5xl md:text-7xl font-light leading-[1] text-white" data-testid="charter-title">
              Every aircraft. <br/><span className="italic text-[#E5C158]">One quote away.</span>
            </h1>
            <p className="text-white/80 mt-6 max-w-xl font-light">
              A curated fleet of vetted operators — with transparent hourly economics, up-to-the-minute availability, and a live ops team on the other end.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/charter/inquiry" className="as-btn-gold px-6 py-3.5 rounded-sm as-mono text-xs uppercase tracking-[0.2em] flex items-center gap-2" data-testid="charter-hero-inquiry">
                Request Custom Quote <ArrowUpRight size={14}/>
              </Link>
              <button
                onClick={() => share({ id: "marketplace", model: "AeroSphere Charter", manufacturer: "", range_nm: "unlimited", max_passengers: "any" }, "facebook")}
                className="px-6 py-3.5 rounded-sm as-mono text-xs uppercase tracking-[0.2em] border border-white/40 text-white hover:border-white hover:bg-white/10 transition-colors flex items-center gap-2"
                data-testid="charter-share-fb"
              >
                <Facebook size={13}/> Share
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FILTER */}
      <div className="sticky top-16 z-30 border-b border-[#E5DFD2] as-glass">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex gap-2 md:gap-3 overflow-x-auto">
          {CATS.map(c => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
              className={`as-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 rounded-sm border transition-all whitespace-nowrap ${
                filter === c ? "border-[#A47E2A] text-[#A47E2A] bg-[#A47E2A]/5" : "border-[#E5DFD2] text-[#756E64] hover:text-[#1A1D24]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* GRID */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-14">
        {loading ? (
          <div className="text-[#756E64] as-mono text-xs">LOADING FLEET…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {list.map((a, i) => (
              <div key={a.id} className="as-card group overflow-hidden as-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`fleet-card-${a.id}`}>
                <div className="aspect-[4/3] overflow-hidden bg-white relative">
                  <img src={a.image_url} alt={a.model} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"/>
                  <div className="absolute top-3 left-3 as-pill as-pill-gold as-mono">{a.category}</div>
                  <div className="absolute top-3 right-3 as-mono text-[10px] uppercase tracking-widest bg-[#FAF7F0]/80 backdrop-blur px-3 py-1.5 rounded-sm">{a.tail_number}</div>
                </div>
                <div className="p-6">
                  <div className="as-mono text-[10px] uppercase tracking-widest text-[#756E64]">{a.manufacturer}</div>
                  <div className="as-serif text-2xl md:text-3xl font-light mt-1">{a.model}</div>
                  <div className="mt-5 grid grid-cols-3 gap-3">
                    <div>
                      <Users size={13} className="text-[#756E64] mb-1"/>
                      <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest">Pax</div>
                      <div className="text-sm">{a.max_passengers}</div>
                    </div>
                    <div>
                      <Route size={13} className="text-[#756E64] mb-1"/>
                      <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest">Range</div>
                      <div className="text-sm">{a.range_nm.toLocaleString()} nm</div>
                    </div>
                    <div>
                      <Gauge size={13} className="text-[#756E64] mb-1"/>
                      <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest">Speed</div>
                      <div className="text-sm">{a.cruise_speed_kts} kts</div>
                    </div>
                  </div>
                  <div className="mt-6 flex items-end justify-between border-t border-[#E5DFD2] pt-5">
                    <div>
                      <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest">Est. Hourly</div>
                      <div className="as-serif text-2xl text-[#A47E2A]">${a.hourly_rate_usd.toLocaleString()}</div>
                    </div>
                    <Link to={`/aircraft/${a.id}`} className="as-btn-gold px-4 py-2.5 rounded-sm as-mono text-[10px] uppercase tracking-[0.2em]" data-testid={`fleet-book-${a.id}`}>
                      Charter
                    </Link>
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="flex gap-2">
                      <button onClick={() => share(a, "instagram")} className="text-[#756E64] hover:text-[#A47E2A] p-1.5" data-testid={`share-ig-${a.id}`}><Instagram size={14}/></button>
                      <button onClick={() => share(a, "facebook")} className="text-[#756E64] hover:text-[#A47E2A] p-1.5" data-testid={`share-fb-${a.id}`}><Facebook size={14}/></button>
                      <button onClick={() => share(a, "share")} className="text-[#756E64] hover:text-[#A47E2A] p-1.5" data-testid={`share-generic-${a.id}`}><Share2 size={14}/></button>
                    </div>
                    <div className="as-mono text-[10px] text-[#756E64] uppercase tracking-widest">Base · {a.base}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA STRIP */}
      <section className="border-t border-[#E5DFD2] py-16 md:py-20 bg-[#FAF7F0]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="as-overline mb-4">Direct-to-operator</div>
            <h3 className="as-serif text-3xl md:text-5xl font-light">Not seeing your aircraft? <span className="italic text-[#A47E2A]">We source anything.</span></h3>
            <p className="text-[#756E64] mt-4 max-w-xl font-light">Access to 40,000+ aircraft globally via preferred operators. Send us your route — you'll have quotes within four hours.</p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/charter/inquiry" className="as-btn-gold px-6 py-4 rounded-sm as-mono text-xs uppercase tracking-[0.2em]" data-testid="charter-bottom-inquiry">
              Request Custom Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
